import React from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';

type CheckboxProps = {
  id: string;
  label: string;
  linkText?: string;
  linkHref?: string;
  linkTarget?: '_self' | '_blank' | '_parent' | '_top';
  linkAriaLabel?: string;
  name?: string;
  description?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  required?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  iconSize: string;
  checkboxSize: string;
  textSize: string;
};

export default function Checkbox({
  id,
  label,
  linkText,
  linkHref,
  linkTarget = '_self',
  linkAriaLabel,
  name,
  description,
  checked,
  defaultChecked,
  disabled = false,
  required = false,
  onChange,
  iconSize,
  checkboxSize,
  textSize,
  className,
}: CheckboxProps) {
  const descriptionId = description ? `${id}-description` : undefined;
  const requiredId = required ? `${id}-required` : undefined;
  const describedBy =
    [descriptionId, requiredId].filter(Boolean).join(' ') || undefined;
  const isControlled = checked !== undefined;

  return (
    <div className="mb-3 flex items-center  gap-3">
      <div className={`flex ${className} shrink-0 items-center`}>
        <div className={`group grid ${checkboxSize} grid-cols-1`}>
          <input
            id={id}
            name={name ?? id}
            type="checkbox"
            aria-describedby={describedBy}
            aria-required={required || undefined}
            disabled={disabled}
            required={required}
            checked={isControlled ? checked : undefined}
            defaultChecked={!isControlled ? defaultChecked : undefined}
            onChange={onChange}
            className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-rgreen checked:bg-rgreen focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rgreen disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
          />
          <CheckIcon
            aria-hidden="true"
            focusable="false"
            className={`pointer-events-none col-start-1 row-start-1 ${iconSize} self-center justify-self-center text-white opacity-0 group-has-checked:opacity-100 group-has-disabled:text-gray-950/25`}
          />
        </div>
      </div>

      <div className={`${textSize} `}>
        <label htmlFor={id} className="font-medium text-black">
          {label}
        </label>

        {required && (
          <span id={requiredId} className="sr-only">
            This field is required.
          </span>
        )}

        {linkText && linkHref && (
          <>
            {' '}
            <a
              href={linkHref}
              target={linkTarget}
              rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
              aria-label={
                linkAriaLabel ??
                (linkTarget === '_blank'
                  ? `${linkText} (opens in a new tab)`
                  : undefined)
              }
              className="font-medium underline text-black hover:text-rgreen focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rgreen"
            >
              {linkText}
            </a>
          </>
        )}

        {description && (
          <>
            {' '}
            <span id={descriptionId} className="text-gray-500">
              {description}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
